import React, { createContext, ReactNode, useContext, useMemo } from 'react';
import type { Country, Currency } from '../graphql/generated/graphql';

interface ReferenceDataContextValue {
  countries: readonly Country[];
  currencies: readonly Currency[];
}

const ReferenceDataContext = createContext<ReferenceDataContextValue>({
  countries: [],
  currencies: []
});

export interface ReferenceDataProviderProps {
  children: ReactNode;
  /** Country list — sourced from GraphQL `countries` query in apps, from fixtures in Storybook. */
  countries: readonly Country[];
  /** Currency list — sourced from GraphQL `currencies` query in apps, from fixtures in Storybook. */
  currencies: readonly Currency[];
}

/**
 * Supplies country and currency reference data to components that need dropdown
 * options and display-name lookups (e.g. CountryEditor, CurrencyEditor, their
 * formatters). The ui-toolkit has no data-fetching layer — the host app is
 * responsible for loading and passing the lists in.
 */
export const ReferenceDataProvider: React.FC<ReferenceDataProviderProps> = ({
  children,
  countries,
  currencies
}) => {
  const value = useMemo(() => ({ countries, currencies }), [countries, currencies]);
  return <ReferenceDataContext.Provider value={value}>{children}</ReferenceDataContext.Provider>;
};

export const useCountries = (): readonly Country[] => useContext(ReferenceDataContext).countries;

export const useCurrencies = (): readonly Currency[] => useContext(ReferenceDataContext).currencies;

/** Look up a country by its ISO 3166-1 alpha-2 code (case-insensitive). */
export function useCountry(code: string | null | undefined): Country | undefined {
  const countries = useCountries();
  if (!code) return undefined;
  const norm = code.trim().toUpperCase();
  return countries.find(c => c.code?.toUpperCase() === norm);
}

/** Look up a currency by its ISO 4217 code (case-insensitive). */
export function useCurrency(code: string | null | undefined): Currency | undefined {
  const currencies = useCurrencies();
  if (!code) return undefined;
  const norm = code.trim().toUpperCase();
  return currencies.find(c => c.code?.toUpperCase() === norm);
}
